import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Zap, Shield, Crown, CreditCard, RefreshCw } from "lucide-react";

const TIER_STYLE = {
  free:  { icon: Shield,  border: "border-[var(--border)]",           badge: null,            cta: "txt-muted border border-[var(--border)] hover:border-white hover:text-white" },
  pro:   { icon: Zap,     border: "border-[var(--accent)]/50",        badge: "MOST POPULAR",  cta: "bg-[var(--accent)] hover:bg-indigo-500 text-white" },
  elite: { icon: Crown,   border: "border-amber-500/40",              badge: "BEST VALUE",    cta: "bg-amber-500 hover:bg-amber-400 text-black" },
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function PayUForm({ params, actionUrl, onDone }) {
  useEffect(() => {
    if (!params || !actionUrl) return;
    const form = document.getElementById("payu-auto-form");
    if (form) { form.submit(); onDone?.(); }
  }, [params, actionUrl, onDone]);

  if (!params) return null;
  return (
    <form id="payu-auto-form" method="POST" action={actionUrl} style={{ display: "none" }}>
      {Object.entries(params).map(([k, v]) => (
        <input key={k} type="hidden" name={k} value={v} />
      ))}
    </form>
  );
}

export default function Pricing() {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [currentTier, setCurrentTier] = useState("free");
  const [gateways, setGateways] = useState({ razorpay: false, payU: false });
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(null);       // which plan is loading
  const [gateway, setGateway] = useState("razorpay"); // selected gateway
  const [payUData, setPayUData] = useState(null);

  useEffect(() => {
    const status = searchParams.get("payment");
    if (status === "success") toast.success("Payment successful! Your plan has been activated.");
    if (status === "failed")  toast.error("Payment failed. Please try again.");
    if (status === "tampered") toast.error("Payment verification failed. Contact support.");
  }, [searchParams]);

  useEffect(() => {
    Promise.all([
      api.get("/payments/plans"),
      api.get("/payments/my-subscription").catch(() => ({ data: { tier: "free" } })),
      api.get("/payments/gateways").catch(() => ({ data: { razorpay: false, payU: false } })),
    ]).then(([p, s, g]) => {
      setPlans(p.data.plans);
      setCurrentTier(s.data.tier || "free");
      const gw = g.data;
      setGateways(gw);
      if (!gw.razorpay && gw.payU) setGateway("payU");
    }).catch(() => toast.error("Failed to load plans"));
  }, []);

  const checkoutRazorpay = useCallback(async (planId) => {
    setLoading(planId);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error("Failed to load Razorpay. Check your connection."); return; }

      const { data } = await api.post("/payments/razorpay/create-order", { plan_id: planId, billing });

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.order_id,
          name: "AlgoForge",
          description: `${data.plan_name} — ${billing === "annual" ? "Annual" : "Monthly"}`,
          image: "/logo192.png",
          theme: { color: "#6366f1" },
          handler: async (response) => {
            try {
              await api.post("/payments/razorpay/verify", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan_id: planId,
                billing,
              });
              toast.success(`Upgraded to ${data.plan_name}! Refreshing…`);
              setTimeout(() => window.location.href = "/settings?payment=success", 1500);
              resolve();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Payment verification failed");
              reject(e);
            }
          },
          modal: { ondismiss: () => resolve() },
        });
        rzp.open();
      });
    } catch (e) {
      if (e?.response?.data?.detail) toast.error(e.response.data.detail);
    } finally {
      setLoading(null);
    }
  }, [billing]);

  const checkoutPayU = useCallback(async (planId) => {
    setLoading(planId);
    try {
      const { data } = await api.post("/payments/payU/initiate", { plan_id: planId, billing });
      setPayUData(data); // PayUForm auto-submits
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to initiate PayU payment");
      setLoading(null);
    }
  }, [billing]);

  const checkout = (planId) => {
    if (gateway === "payU") checkoutPayU(planId);
    else checkoutRazorpay(planId);
  };

  const annualSavingPct = (plan) => {
    if (!plan.price_monthly_inr) return 0;
    const monthlyAnnual = plan.price_annual_inr / 12;
    return Math.round((1 - monthlyAnnual / plan.price_monthly_inr) * 100);
  };

  const displayPrice = (plan) => {
    if (!plan.price_monthly_inr) return "Free";
    if (billing === "annual") return `₹${Math.round(plan.price_annual_inr / 12).toLocaleString("en-IN")}`;
    return `₹${plan.price_monthly_inr.toLocaleString("en-IN")}`;
  };

  if (!plans) {
    return (
      <AppShell>
        <div className="p-8 txt-muted text-center animate-pulse">Loading plans…</div>
      </AppShell>
    );
  }

  const planOrder = ["free", "pro", "elite"];
  const bothGateways = gateways.razorpay && gateways.payU;

  return (
    <AppShell>
      <PageHeader
        overline="Billing"
        title="Choose your plan"
        description="Upgrade to unlock AI Copilot, live trading, options intelligence, and more. Cancel anytime."
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-8 max-w-5xl">

        {/* Billing toggle */}
        <div className="flex items-center gap-4">
          <div className="flex items-center border border-[var(--border)] p-0.5">
            {["monthly", "annual"].map((b) => (
              <button
                key={b}
                onClick={() => setBilling(b)}
                className={`px-5 py-2 text-xs font-section tracking-wider transition-colors ${
                  billing === b
                    ? "bg-[var(--accent)] text-white"
                    : "txt-muted hover:text-[var(--text-primary)]"
                }`}
              >
                {b === "monthly" ? "Monthly" : "Annual"}
              </button>
            ))}
          </div>
          {billing === "annual" && (
            <span className="text-xs font-section tracking-wider text-amber-400 border border-amber-500/30 px-2 py-1">
              SAVE UP TO 20%
            </span>
          )}
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {planOrder.map((key) => {
            const plan = plans[key];
            if (!plan) return null;
            const style = TIER_STYLE[key];
            const Icon = style.icon;
            const isCurrent = currentTier === key;
            const saving = annualSavingPct(plan);

            return (
              <div key={key} className={`panel flex flex-col relative ${style.border} ${key === "pro" ? "shadow-[0_0_32px_rgba(99,102,241,0.1)]" : ""}`}>
                {style.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <span className={`text-[10px] font-section tracking-widest px-3 py-0.5 ${key === "pro" ? "bg-[var(--accent)] text-white" : "bg-amber-500 text-black"}`}>
                      {style.badge}
                    </span>
                  </div>
                )}

                <div className="p-6 flex flex-col flex-1">
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-5">
                    <Icon className={`w-4 h-4 ${key === "pro" ? "txt-accent" : key === "elite" ? "text-amber-400" : "txt-muted"}`} />
                    <span className="overline tracking-widest">{plan.name}</span>
                  </div>

                  {/* Price */}
                  <div className="mb-1">
                    <span className="font-display text-4xl font-bold">{displayPrice(plan)}</span>
                    {plan.price_monthly_inr > 0 && (
                      <span className="txt-muted text-sm ml-1">/mo</span>
                    )}
                  </div>
                  {billing === "annual" && plan.price_monthly_inr > 0 && (
                    <div className="text-xs txt-muted mb-4">
                      Billed ₹{plan.price_annual_inr.toLocaleString("en-IN")}/yr
                      {saving > 0 && <span className="text-amber-400 ml-1">· Save {saving}%</span>}
                    </div>
                  )}
                  {(!plan.price_monthly_inr || billing === "monthly") && <div className="mb-4" />}

                  {/* Features */}
                  <ul className="space-y-2 flex-1 mb-6">
                    {(plan.features || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs">
                        <Check className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${key === "free" ? "txt-muted" : "txt-profit"}`} />
                        <span className="txt-secondary leading-relaxed">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  {isCurrent ? (
                    <div className="text-center text-[10px] font-section tracking-widest txt-muted border border-[var(--border)] py-2.5">
                      CURRENT PLAN
                    </div>
                  ) : key === "free" ? (
                    <div className="text-center text-xs txt-muted py-2.5">Always free</div>
                  ) : (
                    <button
                      onClick={() => checkout(key)}
                      disabled={!!loading}
                      className={`w-full py-2.5 font-section tracking-wider text-xs transition-colors ${style.cta} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {loading === key
                        ? <span className="flex items-center justify-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Processing…</span>
                        : `Upgrade to ${plan.name}`
                      }
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Payment gateway selector */}
        {bothGateways && (
          <div className="panel p-4 max-w-md">
            <div className="overline mb-3">Pay with</div>
            <div className="flex gap-2">
              {[
                { id: "razorpay", label: "Razorpay", sub: "Cards, UPI, Netbanking, Wallets" },
                { id: "payU",     label: "PayU",     sub: "Cards, UPI, EMI, Netbanking" },
              ].map((g) => (
                <button
                  key={g.id}
                  onClick={() => setGateway(g.id)}
                  className={`flex-1 p-3 border text-left transition-colors ${
                    gateway === g.id
                      ? "border-[var(--accent)] bg-[var(--accent-muted)]"
                      : "border-[var(--border)] hover:border-[var(--border-strong)]"
                  }`}
                >
                  <div className={`text-xs font-section tracking-wider mb-0.5 ${gateway === g.id ? "txt-accent" : ""}`}>{g.label}</div>
                  <div className="text-[10px] txt-muted">{g.sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Trust badges */}
        <div className="panel p-5 max-w-3xl">
          <div className="overline mb-3">Payment & Security</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs txt-secondary">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 txt-profit flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-section text-[var(--text-primary)] mb-0.5">Secured Payment</div>
                Razorpay & PayU are PCI-DSS certified. We never store your card details.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Zap className="w-4 h-4 txt-accent flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-section text-[var(--text-primary)] mb-0.5">Instant Activation</div>
                Your plan activates immediately after payment confirmation.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CreditCard className="w-4 h-4 txt-muted flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-section text-[var(--text-primary)] mb-0.5">Cancel Anytime</div>
                No lock-in. Access continues until end of current billing period.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PayU auto-submit form */}
      {payUData && (
        <PayUForm
          actionUrl={payUData.action_url}
          params={payUData.params}
          onDone={() => setLoading(null)}
        />
      )}
    </AppShell>
  );
}
